import { DateTime } from 'luxon'

const BarChart = () => import('@/components/charts/BarChart.vue')
const DoughnutChart = () => import('@/components/charts/DoughnutChart.vue')
const DashboardLoader = () => import('@/components/DashboardLoader.vue')
const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const DashboardError = () => import('@/components/DashboardError.vue')

export default {
    components: { DashboardLoader, DashboardEmpty, DashboardError, BarChart, DoughnutChart },
    data() {
        return {
            othersSliceColor: 'rgb(150, 150, 150)'
        }
    },
    computed: {
        reportURL() {
            if (!this.$route.params.rpid) return ''
            let url = `/resource/${this.$route.params.rpid}`
            if (this.$route.params.oid && !this.$route.params.sid) url += `/org/${this.$route.params.oid}`
            if (this.$route.params.sid) url += `/space/${this.$route.params.sid}`
            return url
        }
    },
    methods: {
        dateToLabel(labels) {
            return labels.map(item => {
                const [year, month, date] = item.split('-')
                return date ? [`${month}-${date}`, year] : [month, year]
            })
        },
        findBarChartGroupingResolution(chartData) {
            const startDate = DateTime.fromISO(Object.keys(chartData)[0])
            const endDate = DateTime.fromISO(Object.keys(chartData).reverse()[0])
            const duration = endDate.diff(startDate, ['days', 'months'])
            return duration.as('days') < 32 ? 'daily' : duration.months < 5 ? 'weekly' : 'monthly'
        },
        dataInGBs(data) {
            return data.map(item => (item === null ? NaN : item / (1000 * 1000 * 1000)))
        },
        roundDataItems(data) {
            return data.map(this.roundDataItem)
        },
        roundDataItem(item) {
            const limit = parseFloat('0.' + '0'.repeat(this.precision - 1) + '1')
            return item < limit && item > 0 ? limit : parseFloat(item.toFixed(this.precision))
        },
        isCurrentMonth(start, end) {
            const now = DateTime.now()
            return (
                start.hasSame(now, 'month') &&
                end.hasSame(now, 'month') &&
                start.hasSame(now, 'year') &&
                end.hasSame(now, 'year') &&
                start.day === 1 &&
                end.day === now.endOf('month').day
            )
        },
        isPreviusMonth(start, end) {
            const now = DateTime.now()
            return (
                start.hasSame(now.minus({ months: 1 }), 'month') &&
                end.hasSame(now.minus({ months: 1 }), 'month') &&
                start.hasSame(now.minus({ months: 1 }), 'year') &&
                end.hasSame(now.minus({ months: 1 }), 'year') &&
                start.day === 1 &&
                end.day === start.endOf('month').day
            )
        }
    },
    watch: {
        reportURL() {
            this.fetchAllCharts()
        },
        dates(newDatePair, oldDatePair) {
            this.$router.push({
                name: this.$route.name,
                params: {
                    ...this.$route.params,
                    startDate: this.dates[0],
                    endDate: this.dates[1]
                },
                hash: this.$route.hash
            })
            const [startDate, endDate] = newDatePair
            const [oldStartDate, oldEndDate] = oldDatePair
            if (startDate === oldStartDate && endDate === oldEndDate) return
            this.fetchAllCharts()
        },
        $route: {
            handler(route) {
                if (route.params.startDate && route.params.endDate) {
                    this.dates = [route.params.startDate, route.params.endDate]
                    const start = DateTime.fromISO(route.params.startDate)
                    const end = DateTime.fromISO(route.params.endDate)

                    // Check if current month
                    if (this.isCurrentMonth(start, end)) {
                        this.selectedDateRangeOption = 'Current month'
                    }
                    // Check if previous month
                    else if (this.isPreviusMonth(start, end)) {
                        this.selectedDateRangeOption = 'Previous month'
                    }
                    // Otherwise custom range
                    else {
                        this.selectedDateRangeOption = 'Custom range'
                    }
                } else {
                    this.selectedDateRangeOption = 'Current month'
                    this.dates = [DateTime.now().startOf('month').toISODate(), DateTime.now().endOf('month').toISODate()]
                }
            },
            immediate: true
        },
        selectedDateRangeOption: {
            handler(dateOption) {
                const now = DateTime.now()
                let monthStart = this.dates[0]
                let monthEnd = this.dates[1]

                if (dateOption === 'Current month') {
                    monthStart = now.startOf('month').toISODate()
                    monthEnd = now.endOf('month').toISODate()
                }

                if (dateOption === 'Previous month') {
                    const prevMonth = now.minus({ months: 1 })
                    monthStart = prevMonth.startOf('month').toISODate()
                    monthEnd = prevMonth.endOf('month').toISODate()
                }

                this.dates = [monthStart, monthEnd]
            }
        }
    }
}
